import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import GameInfoPopup from '../../../components/GameInfoPopup/GameInfoPopup';
import StageHud from '../../../components/StageHud/StageHud';
import { GAME_COPY, GAME_KEYS, STAGE4_QUESTIONS } from '../../../data/games';
import { finishGame } from '../../../utils/gameSave';
import { useCarnivalTimer } from '../../../hooks/useCarnivalTimer';
import { useStagePoints } from '../../../hooks/useStagePoints';
import './DiversityQuizPage.css';

export default function DiversityQuizPage() {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);
  const copy = GAME_COPY.stage4;

  const [pos, setPos] = useState(0);
  const [value, setValue] = useState('');
  const [fieldStatus, setFieldStatus] = useState(null);
  const [score, setScore] = useState(0);
  const [popupSrc, setPopupSrc] = useState(null);
  const [busy, setBusy] = useState(false);
  const scoreRef = useRef(0);
  const doneRef = useRef(false);
  const pendingAdvance = useRef(false);

  const question = STAGE4_QUESTIONS[pos];
  const isLast = pos === STAGE4_QUESTIONS.length - 1;
  const { displayPoints, alreadyPlayed, markPlayedAndSyncCoins } = useStagePoints(GAME_KEYS.stage4, score);
  const { display: timerDisplay, elapsedRef } = useCarnivalTimer({ running: !alreadyPlayed });

  const onType = (raw) => {
    setValue(raw);
    const trimmed = raw.trim();
    if (!trimmed) {
      setFieldStatus(null);
      return;
    }
    setFieldStatus(trimmed.toLowerCase() === question.ans ? 'correct' : 'wrong');
  };

  const submitAll = async (finalScore) => {
    if (doneRef.current || busy || !user) return;
    doneRef.current = true;
    setBusy(true);
    try {
      const result = await finishGame({
        user,
        gameKey: GAME_KEYS.stage4,
        score: finalScore,
        elapsedSeconds: elapsedRef.current,
      });
      markPlayedAndSyncCoins(result);
      if (result.alreadyPlayed) navigate('/hub');
      else navigate(`/thank-you?game=${GAME_KEYS.stage4}&points=${result.coinsEarned ?? 0}`);
    } catch (err) {
      console.error(err);
      doneRef.current = false;
      setBusy(false);
      Swal.fire({
        title: 'Could not save score',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: { confirmButton: 'hf-swal-close', popup: 'hf-swal-popup' },
        buttonsStyling: false,
      });
    }
  };

  const onNext = () => {
    const trimmed = value.trim();
    if (!trimmed) {
      Swal.fire({
        title: 'Please fill the answer',
        icon: 'warning',
        confirmButtonText: 'OK',
        customClass: { confirmButton: 'hf-swal-close', popup: 'hf-swal-popup' },
        buttonsStyling: false,
      });
      return;
    }

    const correct = trimmed.toLowerCase() === question.ans;
    if (correct) {
      scoreRef.current += 1;
      setScore(scoreRef.current);
      setFieldStatus('correct');
    } else {
      setFieldStatus('wrong');
    }

    pendingAdvance.current = true;
    setPopupSrc(question.popup);
  };

  const closePopup = () => {
    setPopupSrc(null);
    if (!pendingAdvance.current) return;
    pendingAdvance.current = false;
    if (isLast) {
      submitAll(scoreRef.current);
      return;
    }
    setPos((p) => p + 1);
    setValue('');
    setFieldStatus(null);
  };

  return (
    <div
      className="diversity-page stage-bg"
      style={{
        '--ic-bg': 'url(/img/BG_2.jpg)',
        '--ic-art': 'url(/stage4/img/stage2bg.jpg)',
      }}
    >
      <div className="diversity-stage-art" aria-hidden="true" />
      <div className="diversity-alldata">
        <StageHud
          title={copy.title}
          instruction={copy.instruction}
          timerDisplay={timerDisplay}
          points={displayPoints}
          rules={copy.rules}
        />

        <div className="diversity-frame">
          <img src={question.image} alt="" className="main-image" />
          <input
            type="text"
            className={`input-text1 ${fieldStatus || ''}`}
            value={value}
            onChange={(e) => onType(e.target.value)}
            autoComplete="off"
          />
          <button type="button" className="next-button" disabled={busy} onClick={onNext}>
            {isLast ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>

      {popupSrc && (
        <GameInfoPopup
          imageSrc={popupSrc}
          buttonLabel={isLast ? 'Finish' : 'Next'}
          onNext={closePopup}
        />
      )}
    </div>
  );
}
